import { useState } from 'react';
import { API_ENDPOINTS } from '../config/apiEndpoints';

/**
 * Hook für zentrale API-Anfragen
 * Stellt standardisierte Funktionen für API-Aufrufe bereit
 * 
 * @returns {Object} API-Hilfsfunktionen und Zustände
 */
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('accessToken');

  /**
   * Führt einen API-Aufruf mit Standardeinstellungen durch
   * 
   * @param {string} url - Die URL für den API-Aufruf
   * @param {Object} options - Die Optionen für den fetch-Aufruf
   * @returns {Promise<Object>} Die API-Antwort
   */
  const fetchApi = async (url, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Request-Headers': '*',
          ...options.headers,
        },
      });
      
      const result = await response.json();
      
      if (result.error) {
        setError(result.error);
        throw new Error(result.error);
      }
      
      return result;
    } catch (err) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * GET-Anfrage mit Token
   * 
   * @param {string} url - Die API-URL
   * @returns {Promise<Object>} Die API-Antwort
   */
  const get = async (url) => {
    return fetchApi(url, {
      method: 'GET',
    });
  };

  /**
   * POST-Anfrage mit Token und Body
   * 
   * @param {string} url - Die API-URL
   * @param {Object} body - Die zu sendenden Daten
   * @returns {Promise<Object>} Die API-Antwort
   */
  const post = async (url, body = {}) => {
    return fetchApi(url, {
      method: 'POST',
      body: JSON.stringify({ ...body, token }),
    });
  };

  /**
   * Lädt Projekte vom Server
   * 
   * @returns {Promise<Array>} Liste der Projekte
   */
  const fetchProjects = async () => {
    return post(API_ENDPOINTS.GET_PROJECTS);
  };

  /**
   * Fügt ein neues Projekt hinzu
   * 
   * @param {Object} projectData - Die Daten des neuen Projekts
   * @returns {Promise<Object>} Die API-Antwort
   */
  const addProject = async (projectData) => {
    return post(API_ENDPOINTS.ADD_PROJECT, { projectData });
  };

  /**
   * Aktualisiert ein bestehendes Projekt
   * 
   * @param {string} projectId - Die ID des zu aktualisierenden Projekts
   * @param {Object} projectData - Die neuen Projektdaten
   * @returns {Promise<Object>} Die API-Antwort
   */
  const updateProject = async (projectId, projectData) => {
    return post(API_ENDPOINTS.CHANGE_PROJECT, { projectId, projectData });
  };

  /**
   * Löscht ein Projekt
   * 
   * @param {string} projectId - Die ID des zu löschenden Projekts
   * @returns {Promise<Object>} Die API-Antwort
   */
  const deleteProject = async (projectId) => {
    return post(API_ENDPOINTS.DELETE_PROJECT, { projectId });
  };

  /**
   * Lädt Kunden vom Server
   * 
   * @returns {Promise<Array>} Liste der Kunden
   */
  const fetchClients = async () => {
    return post(API_ENDPOINTS.GET_CLIENTS);
  };

  /**
   * Fügt einen neuen Kunden hinzu
   * 
   * @param {Object} clientData - Die Daten des neuen Kunden
   * @returns {Promise<Object>} Die API-Antwort
   */
  const addClient = async (clientData) => {
    return post(API_ENDPOINTS.ADD_CLIENT, { clientData });
  };

  /**
   * Aktualisiert einen bestehenden Kunden
   * 
   * @param {string} clientId - Die ID des zu aktualisierenden Kunden
   * @param {Object} clientData - Die neuen Kundendaten
   * @returns {Promise<Object>} Die API-Antwort
   */
  const updateClient = async (clientId, clientData) => {
    return post(API_ENDPOINTS.CHANGE_CLIENT, { clientId, clientData });
  };

  /**
   * Löscht einen Kunden
   * 
   * @param {string} clientId - Die ID des zu löschenden Kunden
   * @returns {Promise<Object>} Die API-Antwort
   */
  const deleteClient = async (clientId) => {
    return post(API_ENDPOINTS.DELETE_CLIENT, { clientId });
  };

  /**
   * Lädt Aufgaben vom Server
   * 
   * @returns {Promise<Array>} Liste der Aufgaben
   */
  const fetchTasks = async () => {
    return post(API_ENDPOINTS.GET_TASKS);
  };

  /**
   * Fügt eine neue Aufgabe hinzu
   * 
   * @param {Object} taskData - Die Daten der neuen Aufgabe
   * @returns {Promise<Object>} Die API-Antwort
   */
  const addTask = async (taskData) => {
    return post(API_ENDPOINTS.ADD_TASK, { taskData });
  };

  /**
   * Aktualisiert eine bestehende Aufgabe
   * 
   * @param {string} taskId - Die ID der zu aktualisierenden Aufgabe
   * @param {Object} taskData - Die neuen Aufgabendaten
   * @returns {Promise<Object>} Die API-Antwort
   */
  const updateTask = async (taskId, taskData) => {
    return post(API_ENDPOINTS.CHANGE_TASK, { taskId, taskData });
  };

  /**
   * Löscht eine Aufgabe
   * 
   * @param {string} taskId - Die ID der zu löschenden Aufgabe
   * @returns {Promise<Object>} Die API-Antwort
   */
  const deleteTask = async (taskId) => {
    return post(API_ENDPOINTS.DELETE_TASK, { taskId });
  };

  /**
   * Lädt Mitarbeiter vom Server
   * 
   * @returns {Promise<Object>} Mitarbeiterdaten
   */
  const fetchEmployeeData = async () => {
    return post(API_ENDPOINTS.EMPLOYEE);
  };

  /**
   * Lädt Liste aller Mitarbeiter vom Server
   * 
   * @returns {Promise<Array>} Liste der Mitarbeiter
   */
  const fetchEmployeeList = async () => {
    return post(API_ENDPOINTS.EMPLOYEE_LIST);
  };

  /**
   * Lädt Zeitklassen vom Server
   * 
   * @returns {Promise<Array>} Liste der Zeitklassen
   */
  const fetchTimeClasses = async () => {
    return get(API_ENDPOINTS.GET_TIME_CLASSES);
  };

  /**
   * Lädt die Vertragsmodelle vom Server
   * 
   * @returns {Promise<Array>} Liste der Vertragsmodelle
   */
  const fetchContractModels = async () => {
    return get(API_ENDPOINTS.GET_CONTRACT_MODELS);
  };

  return {
    // Status
    loading,
    error,
    
    // Basismethoden
    fetchApi,
    get,
    post,
    
    // Projekte
    fetchProjects,
    addProject,
    updateProject,
    deleteProject,
    
    // Kunden
    fetchClients,
    addClient,
    updateClient,
    deleteClient,
    
    // Aufgaben
    fetchTasks,
    addTask,
    updateTask,
    deleteTask,
    
    // Mitarbeiter
    fetchEmployeeData,
    fetchEmployeeList,
    
    // Andere Daten
    fetchTimeClasses,
    fetchContractModels
  };
};
